// Step 1 workspace: capture/edit the product idea, aspiration, and customer
// segment, with the idea coach on hand. New projects land here (first
// not-done step) and get the one-time journey intro.

import { useState } from 'react';
import { Loader2, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useProject, useUpdateProject } from '@/hooks/use-projects';
import { IdeaCoachDialog, IdeaCoachPanel } from '@/features/dialog/IdeaCoachChat';
import { useUIStore } from '@/stores';
import { toast } from '@/hooks/use-toast';
import { isIdeaComplete } from '@/features/idea/idea-status';
import { isFramingCurrent } from '@/features/idea/framing-state';
import { StepHeader } from '@/features/project/StepHeader';
import { SectionLabel } from '@/components/ui/section-label';
import { ClampText } from '@/components/ui/clamp-text';
import { WORKSPACE_WIDTH_NARROW } from '@/lib/workspace-width';
import type { IdeaDraft } from '@/types/dialog';
import type { Project } from '@/types/project';

interface IdeaViewProps {
  projectId: string;
}

export function IdeaView({ projectId }: IdeaViewProps) {
  const { data: project } = useProject(projectId);
  // The workspace body owns mount-time editing state, so don't render it until
  // the project is loaded. Usually the cache is already warm; the spinner only
  // shows on a cold ?view=idea deep-link.
  if (!project) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading project" />
      </div>
    );
  }
  return <IdeaWorkspace projectId={projectId} project={project} />;
}

function IdeaWorkspace({ projectId, project }: { projectId: string; project: Project }) {
  const hasSeenJourneyIntro = useUIStore((s) => s.hasSeenJourneyIntro);
  const setHasSeenJourneyIntro = useUIStore((s) => s.setHasSeenJourneyIntro);
  // Auto-open the form when the step is incomplete on mount so the PM doesn't
  // have to hunt for an Edit button. Read completeness only at mount (not via
  // an effect): a partial Save/Cancel that leaves the step "in progress" should
  // collapse to the summary, not spring the form back open. Done projects
  // render the summary; clicking Edit re-opens the form pre-filled.
  const [editing, setEditing] = useState(() => !isIdeaComplete(project));
  const [coachOpen, setCoachOpen] = useState(false);
  // Coach output, applied into the form. `seedKey` bumps on each apply so the
  // form remounts and re-seeds even if it was already open in edit mode.
  const [seed, setSeed] = useState<IdeaDraft | null>(null);
  const [seedKey, setSeedKey] = useState(0);
  // Coach-first front door: a project with nothing captured yet lands in the
  // coach, with a quiet manual escape hatch. Once anything is captured (or
  // the coach applies a draft), the form/summary owns the surface.
  const [manualMode, setManualMode] = useState(false);
  const hasAnyIdeaContent = !!(
    project.idea?.trim() ||
    project.aspiration?.trim() ||
    project.customerSegment?.role?.trim()
  );
  const coachFirst = !hasAnyIdeaContent && !manualMode && !seed;
  // Mirrors the step-status done rule so the pine action yields to the next-step bar.
  const ideaDone = isIdeaComplete(project) && isFramingCurrent(project);

  const handleApply = (draft: IdeaDraft) => {
    setSeed(draft);
    setSeedKey((k) => k + 1);
    setEditing(true);
    setCoachOpen(false);
  };

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className={`${WORKSPACE_WIDTH_NARROW} mx-auto space-y-6`}>
        {/* One-time orientation — new projects land here, and this is the only
            place the journey-arc prose lives now the journey page is retired.
            The rail carries the roadmap; the Live graph panel shows the accretion. */}
        {!hasSeenJourneyIntro && (
          <div className="relative rounded-lg border bg-card p-4 pr-10 text-sm">
            <p className="max-w-prose text-muted-foreground">
              <span className="font-medium text-foreground">How this works:</span> eight steps take
              your idea to a pitch deck you can defend. Along the way you'll check the market and
              talk to potential customers. Everything you learn lands on the graph, which ties each
              claim to the evidence behind it. The rail on the left is your roadmap. Click a step to
              move around.
            </p>
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 h-7 w-7"
              onClick={() => setHasSeenJourneyIntro(true)}
              title="Dismiss"
              aria-label="Dismiss intro"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        <StepHeader
          projectId={projectId}
          stepId="idea"
          actions={
            !coachFirst ? (
              <Button variant={ideaDone ? 'outline' : 'brand'} className="shrink-0" onClick={() => setCoachOpen(true)}>
                <Sparkles className="h-4 w-4 mr-2" />
                Chat with an idea coach
              </Button>
            ) : undefined
          }
        />

        {coachFirst ? (
          <div className="space-y-2">
            <div className="rounded-lg border bg-card h-[500px] flex flex-col overflow-hidden">
              <IdeaCoachPanel projectId={projectId} onApply={handleApply} />
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Know exactly what you want to capture?{' '}
              <button className="underline hover:text-foreground" onClick={() => setManualMode(true)}>
                Enter it manually instead
              </button>
            </p>
          </div>
        ) : editing ? (
          <div className="rounded-lg border bg-card p-5">
            <IdeaCaptureForm
            key={seedKey}
            projectId={projectId}
            project={project}
            seed={seed}
            onDone={() => {
              setEditing(false);
              setSeed(null);
            }}
          />
          </div>
        ) : (
          <div className="rounded-lg border bg-card p-5 space-y-7 text-base">
            <SummaryRow label="Product idea" value={project.idea} />
            {/* The aspiration is the outcome in the customer's words — the
                quotes encode that it's a voice, not our copy. */}
            <div>
              <SectionLabel>Aspiration</SectionLabel>
              {project.aspiration ? (
                <ClampText text={`“${project.aspiration}”`} className="mt-0.5 italic" />
              ) : (
                <div className="mt-0.5 text-muted-foreground italic">Not set</div>
              )}
            </div>
            {/* The segment is structured data — show the structure instead of
                joining a sentence-long description into the dotted pair. */}
            <div>
              <SectionLabel>Customer segment</SectionLabel>
              {project.customerSegment?.role ? (
                <div className="mt-1 space-y-1">
                  {/* Key-value data gets its keys: an industry name alone
                      ("SaaS") doesn't say whose industry it is. */}
                  <SegmentField label="Role" value={project.customerSegment.role} />
                  {project.customerSegment.industry && (
                    <SegmentField label="Industry" value={project.customerSegment.industry} />
                  )}
                  {project.customerSegment.description && (
                    <ClampText
                      text={project.customerSegment.description}
                      className="text-sm text-muted-foreground"
                    />
                  )}
                </div>
              ) : (
                <div className="mt-0.5 text-muted-foreground italic">Not set</div>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              Edit
            </Button>
          </div>
        )}

        <IdeaCoachDialog
          projectId={projectId}
          isOpen={coachOpen}
          onClose={() => setCoachOpen(false)}
          onApply={handleApply}
        />
      </div>
    </div>
  );
}

// Quiet key beside a segment value — sentence case and muted so it labels
// without competing with the section label above.
function SegmentField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="w-16 shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0">{value}</span>
    </div>
  );
}

export function SummaryRow({ label, value }: { label: string; value?: string }) {
  return (
    <div>
      <SectionLabel>{label}</SectionLabel>
      {value ? (
        <ClampText text={value} className="mt-0.5" />
      ) : (
        <div className="mt-0.5 text-muted-foreground italic">Not set</div>
      )}
    </div>
  );
}

interface IdeaCaptureFormProps {
  projectId: string;
  project: Project;
  // Coach-captured values to pre-fill. A captured field wins; a field the coach
  // didn't capture keeps the existing project value so the PM doesn't lose what
  // they already had. The PM reviews + saves through the normal path.
  seed?: IdeaDraft | null;
  onDone: () => void;
}

function IdeaCaptureForm({ projectId, project, seed, onDone }: IdeaCaptureFormProps) {
  const updateProject = useUpdateProject();
  const [idea, setIdea] = useState(seed?.idea ?? project.idea ?? '');
  const [aspiration, setAspiration] = useState(seed?.aspiration ?? project.aspiration ?? '');
  const [segmentRole, setSegmentRole] = useState(seed?.customerSegment?.role ?? project.customerSegment?.role ?? '');
  const [segmentIndustry, setSegmentIndustry] = useState(seed?.customerSegment?.industry ?? project.customerSegment?.industry ?? '');
  const [segmentDescription, setSegmentDescription] = useState(
    seed?.customerSegment?.description ?? project.customerSegment?.description ?? '',
  );

  const handleSubmit = async () => {
    // null clears a previously-saved value; the backend merge deletes null
    // keys (undefined would be stripped by JSON.stringify and silently leave
    // the old value intact). customerSegment requires a non-empty role at the
    // schema level, so a blank role clears the whole segment.
    const segment = segmentRole.trim()
      ? {
          role: segmentRole.trim(),
          industry: segmentIndustry.trim() || undefined,
          description: segmentDescription.trim() || undefined,
        }
      : null;
    try {
      await updateProject.mutateAsync({
        projectId,
        patch: {
          idea: idea.trim() || null,
          aspiration: aspiration.trim() || null,
          customerSegment: segment,
        },
      });
      onDone();
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to save.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-3">
      <Field label="Product idea" hint="What are you building? Bullet points work well for the detail.">
        <textarea
          value={idea}
          onChange={(e) => setIdea(e.target.value)}
          rows={2}
          maxLength={2000}
          className="w-full px-3 py-2 border rounded-md bg-background focus:outline-hidden focus:ring-2 focus:ring-ring resize-none text-sm"
        />
      </Field>
      <Field label="Aspiration" hint="The outcome your customer wants, in their words (e.g. peel fruit efficiently). Not your method or product.">
        <input
          type="text"
          value={aspiration}
          onChange={(e) => setAspiration(e.target.value)}
          maxLength={500}
          className="w-full px-3 py-2 border rounded-md bg-background focus:outline-hidden focus:ring-2 focus:ring-ring text-sm"
        />
      </Field>
      <div className="border-t pt-3 space-y-2">
        <p className="text-sm font-medium">Customer segment</p>
        <input
          type="text"
          value={segmentRole}
          onChange={(e) => setSegmentRole(e.target.value)}
          placeholder="Target role (e.g. Field ops leads, Home cooks)"
          maxLength={200}
          className="w-full px-3 py-2 border rounded-md bg-background focus:outline-hidden focus:ring-2 focus:ring-ring text-sm"
        />
        <input
          type="text"
          value={segmentIndustry}
          onChange={(e) => setSegmentIndustry(e.target.value)}
          placeholder="Industry (optional)"
          maxLength={200}
          className="w-full px-3 py-2 border rounded-md bg-background focus:outline-hidden focus:ring-2 focus:ring-ring text-sm"
        />
        <textarea
          value={segmentDescription}
          onChange={(e) => setSegmentDescription(e.target.value)}
          placeholder="Anything else about the segment (optional)"
          rows={2}
          maxLength={1000}
          className="w-full px-3 py-2 border rounded-md bg-background focus:outline-hidden focus:ring-2 focus:ring-ring resize-none text-sm"
        />
      </div>
      <div className="flex gap-2 pt-1">
        <Button size="sm" onClick={handleSubmit} disabled={updateProject.isPending}>
          {updateProject.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
          Save
        </Button>
        <Button variant="outline" size="sm" onClick={onDone} disabled={updateProject.isPending}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <div>
        <span className="text-sm font-medium">{label}</span>
        {hint && <span className="text-xs text-muted-foreground ml-2">{hint}</span>}
      </div>
      {children}
    </label>
  );
}
